import { createContext } from 'react'
import type { MissingRows } from './missing'
import type {
  FlightLeg,
  ImportBatch,
  ImportPlan,
  ImportScope,
  ParsedFile,
  ScheduleErrorCode,
} from './types'

export type ScheduleStatus = 'loading' | 'ready' | 'error'

/** A file that has been read and joined to reference data, not yet written. */
export interface PreparedFile {
  parsed: ParsedFile
  /** Period to replace. Starts at what the file declares; the user may narrow it. */
  scope: ImportScope
  legs: FlightLeg[]
}

export interface ScheduleContextValue {
  status: ScheduleStatus
  providerName: string
  legs: FlightLeg[]
  batches: ImportBatch[]
  /** False when the last write did not reach storage. */
  persisted: boolean
  canUndo: boolean
  /** Reads a file and joins it to the reference tables. Never writes. */
  prepare(parsed: ParsedFile): PreparedFile
  /** What these files would do to the loaded schedule. Never writes. */
  plan(files: PreparedFile[]): ImportPlan
  /** Tails and stations these files use that the reference tables lack. */
  missing(files: PreparedFile[]): MissingRows
  /**
   * Writes the import.
   *
   * `created` carries the reference rows the caller has just added for this
   * import. They are applied to the clock before the legs are resolved again,
   * because a station's zone is what places its legs, and waiting for the
   * reference tables to come back through React state would resolve them
   * against the table as it was a moment ago.
   */
  commit(files: PreparedFile[], created?: MissingRows): Promise<ImportPlan>
  undo(): Promise<void>
  clear(): Promise<void>
}

export const ScheduleContext = createContext<ScheduleContextValue | null>(null)

/** The UI owns the wording; the provider only raises codes. */
export const SCHEDULE_ERROR_MESSAGE: Record<ScheduleErrorCode, string> = {
  unreadable_file: 'Not a readable .xlsx file.',
  unknown_layout: 'No DATE / FLT / DEP / ARR header row.',
  empty_file: 'No flight rows.',
  storage_full: 'Local storage full.',
  unsupported_browser: 'This browser cannot read .xlsx.',
  unknown: 'Something went wrong.',
}
